import { z } from "zod";
import { currencyCode, hexColor, isoDate, moneyMinor, optionalText, text, uuid } from "./common";

export const taxCategoryInput = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_]{1,30}$/),
  name: text(100),
  description: optionalText(500),
  isActive: z.boolean().default(true),
});
export const taxRateInput = z
  .object({
    name: text(100),
    component: z.string().trim().toUpperCase().regex(/^[A-Z][A-Z0-9_]{0,19}$/, "e.g. GST, CGST, VAT"),
    rate: z.number().min(0).max(100).multipleOf(0.0001),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullable().default(null),
  })
  .refine((r) => !r.effectiveTo || r.effectiveTo >= r.effectiveFrom, { message: "End date is before start date", path: ["effectiveTo"] });

export interface TaxRate {
  id: string;
  name: string;
  component: string;
  rate: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}
export interface TaxCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  rates: TaxRate[];
}

export const serviceCategoryInput = z.object({
  name: text(100),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
  isActive: z.boolean().default(true),
});
export interface ServiceCategory {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export const serviceInput = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9_-]{0,29}$/),
  name: text(200),
  description: optionalText(2000),
  categoryId: uuid.nullable().default(null),
  durationMinutes: z.number().int().min(5).max(480),
  taxCategoryId: uuid.nullable().default(null),
  priceIncludesTax: z.boolean().default(false),
  /** Printed on invoice lines (India: SAC code, e.g. 999312). */
  taxClassificationCode: z.string().trim().toUpperCase().regex(/^[A-Z0-9.]{2,12}$/, "e.g. 999312").nullish().or(z.literal("")).transform((v) => v || null),
  isBookableOnline: z.boolean().default(false),
  requiresTooth: z.boolean().default(false),
  calendarColor: hexColor.nullable().default(null),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
  isActive: z.boolean().default(true),
  /** Empty = offered at every active branch. */
  branchIds: z.array(uuid).max(200).default([]),
  doctors: z
    .array(z.object({ doctorId: uuid, durationMinutes: z.number().int().min(5).max(480).nullable().default(null), priceMinorOverride: moneyMinor.nullable().default(null) }))
    .max(500)
    .default([]),
  /** Initial tenant-wide price; later changes go through the price endpoint (effective-dated). */
  priceMinor: moneyMinor.optional(),
});
export const updateServiceInput = serviceInput.omit({ priceMinor: true }).extend({ version: z.number().int().min(1) });

export const servicePriceInput = z.object({
  branchId: uuid.nullable().default(null),
  priceMinor: moneyMinor,
  currencyCode: currencyCode.optional(),
  effectiveFrom: isoDate,
});

export interface ServicePrice {
  id: string;
  branchId: string | null;
  priceMinor: number;
  currencyCode: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface Service {
  id: string;
  code: string;
  name: string;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  durationMinutes: number;
  taxCategoryId: string | null;
  priceIncludesTax: boolean;
  taxClassificationCode: string | null;
  isBookableOnline: boolean;
  requiresTooth: boolean;
  calendarColor: string | null;
  sortOrder: number;
  isActive: boolean;
  branchIds: string[];
  doctors: { doctorId: string; durationMinutes: number | null; priceMinorOverride: number | null }[];
  /** Today's tenant-wide price, if any. */
  currentPriceMinor: number | null;
  prices: ServicePrice[];
  version: number;
}
